import { useId, useState, type FocusEvent } from 'react';
import { Box, FormControlLabel, FormHelperText, Stack, Typography } from '@mui/material';
import { fieldTypeOf, fitsFieldType } from '../../field-types';
import type { FieldElement } from '../../format/form-definition';
import { AUTHOR_TEXTS } from '../../texts/author';
import { isFilled } from '../../validations';
import { useBuilder } from '../context';
import { sameJson } from '../same-json';
import { DrawerSection } from '../drawer/DrawerSection';
import { fieldInputOf } from '../../rendering/fieldInput';
import { optionsOf } from '../../format/elements';
import { Button } from 'framework/ui/mui-builder';
import { UNTITLED } from '../canvas/CanvasField';
import { fieldPreview } from '../fieldPreview';

export const DefaultValueSection = ({ field }: { field: FieldElement }) => {
  const { edit } = useBuilder();
  const [clears, setClears] = useState(0);
  const id = useId();
  const inputId = `${id}input`;
  const labelId = `${id}label`;
  const card = fieldTypeOf(field);
  if (!card.hasDefaultValue) return null;
  const clear = field.defaultValue !== undefined && (
    <Button
      variant={'text'}
      size="small"
      onClick={() => {
        edit({ kind: 'setDefaultValue', fieldId: field.id, value: card.emptyValue });
        setClears(n => n + 1);
      }}
    >
      Clear
    </Button>
  );
  return (
    <DrawerSection
      sections={[
        {
          id: 'defaultValue',
          title: (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography component="label" htmlFor={inputId} id={labelId}>
                Default value
              </Typography>
              {clear}
            </Stack>
          ),
          content: <DefaultValueInput key={clears} field={field} inputId={inputId} labelId={labelId} />,
        },
      ]}
    />
  );
};

const DefaultValueInput = ({ field, inputId, labelId }: { field: FieldElement; inputId: string; labelId: string }) => {
  const { form, edit } = useBuilder();
  const preview = fieldPreview(form, field);
  const { card, settings, Input, emptyValue, labelKind } = fieldInputOf(field);
  const [unfinished, setUnfinished] = useState<{ value: unknown } | null>(null);
  const noteId = `${inputId}note`;

  const onChange = (value: unknown) => {
    if (!fitsFieldType(field, value)) {
      setUnfinished({ value });
      return;
    }
    setUnfinished(null);
    edit({ kind: 'setDefaultValue', fieldId: field.id, value });
  };
  const storeOnLeaving = (event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    const value: unknown = field.defaultValue;
    if (!card.stored || !isFilled(card, value)) return;
    const stored = card.stored(value, settings);
    if (!sameJson(stored, value)) edit({ kind: 'setDefaultValue', fieldId: field.id, value: stored });
  };

  const input = (
    <Input
      inputId={inputId}
      labelId={labelId}
      describedBy={unfinished ? noteId : undefined}
      settings={settings}
      options={optionsOf(field)}
      label="Default value"
      value={unfinished ? unfinished.value : (field.defaultValue ?? emptyValue)}
      onChange={onChange}
      onBlur={() => {}}
      disabled={false}
      required={preview.required ?? false}
      error={false}
      bounds={preview.bounds ?? {}}
      attachFile={() => {
        throw new Error('The Builder attaches no files.');
      }}
    />
  );

  return (
    <Box onBlur={storeOnLeaving}>
      {labelKind === 'beside' ? (
        <FormControlLabel control={input} label={field.label.trim() ? field.label : UNTITLED.field} />
      ) : (
        input
      )}
      {unfinished && <FormHelperText id={noteId}>{AUTHOR_TEXTS.defaultValue.unfinished}</FormHelperText>}
    </Box>
  );
};
